import type { Signal } from '@angular/core'
import { computed, Injectable, inject } from '@angular/core'
import { WorkspaceStore } from '@awork/features/workspace/state/workspace.store'
import type { Workspace, WorkspaceFeature } from '@awork/features/workspace/models/workspace.model'
import { EntitySignalQuery } from '@awork/core/state/signal-store/entitySignalQuery'
import { Order } from '@awork/core/state/signal-store/types'
import { AccountQuery } from '@awork/_shared/state/account.query'

@Injectable({ providedIn: 'root' })
export class WorkspaceQuery extends EntitySignalQuery<Workspace> {
  protected store: WorkspaceStore
  private readonly accountQuery = inject(AccountQuery)

  constructor() {
    const store = inject(WorkspaceStore)

    super(store)

    this.store = store
  }

  /**
   * Gets current workspace
   * @returns {Workspace}
   */
  getCurrentWorkspace(): Workspace {
    const currentWorkspace = this.getActive() as Workspace
    return this.mapEntity(currentWorkspace)
  }

  /**
   * Gets current workspace
   * @returns {Signal<Workspace>}
   */
  queryCurrentWorkspace(): Signal<Workspace> {
    return computed(() => {
      const currentWorkspace = this.queryActive()
      return currentWorkspace() ? currentWorkspace() : null
    })
  }

  /**
   * Names the products removed by workspace cancellation or deletion.
   * @returns {Signal<string>} awork AI for AI-only workspaces, otherwise awork.
   */
  queryProductName(): Signal<string> {
    const workspace = this.queryCurrentWorkspace()

    return computed(() => {
      const products = workspace()?.products

      return products?.includes('ai') && !products.includes('pm') ? 'awork AI' : 'awork'
    })
  }

  /**
   * Checks whether the account timezone differs from the current workspace timezone.
   * Optional timezone choices show only then, so teams in one timezone keep simple views.
   * @returns {Signal<boolean>}
   */
  queryHasOtherTimezone(): Signal<boolean> {
    const workspace = this.queryCurrentWorkspace()
    const accountTimezone = this.accountQuery.queryTimezone()

    return computed(() => accountTimezone() !== (workspace()?.timezone || 'Europe/Berlin'))
  }

  /**
   * Selects all workspaces
   * @returns {Signal<Workspace[]>}
   */
  queryAllWorkspaces(): Signal<Workspace[]> {
    return this.queryAll({
      sortBy: 'lastLogin',
      sortByOrder: Order.DESC
    })
  }

  /**
   * Gets all workspaces
   * @returns {Workspace[]}
   */
  getAllWorkspaces(): Workspace[] {
    const workspaces = this.getAll({
      sortBy: 'lastLogin',
      sortByOrder: Order.DESC
    })

    return this.mapEntities(workspaces)
  }

  /**
   * Checks if a feature is enabled for the workspace
   * @param {WorkspaceFeature} feature
   * @returns {boolean}
   */
  isFeatureEnabled(feature: WorkspaceFeature): boolean {
    return this.getCurrentWorkspace()?.isFeatureEnabled(feature)
  }
}
